import { puzzelLogin } from "api/puzzel/puzzel";
import { ensurePuzzelAuthenticated } from "app/PuzzelAuth/puzzelAuth";
import { isPuzzelOidcEnabled } from "app/PuzzelAuth/puzzelAuthConfig";
import {
  getPuzzelCustomerKey,
  invalidatePuzzelAccessToken,
  resetPuzzelLoginAttempts,
} from "app/PuzzelAuth/puzzelTokenStorage";
import axios from "axios";
import { log, LogLevel } from "logging/datadogBrowserLogs";
import { devLog } from "shared/utils/devLog";

const PUZZEL_BASE_URL = "https://api.puzzel.com/ContactCentre5/";

// Legacy silent-login token storage keys - remove them both once puzzelOidcLogin is rolled out to everyone.
// Deliberately distinct from the OIDC keys in puzzelTokenStorage.ts so the two auth modes never
// read/write the same localStorage slot while both exist behind the flag.
export const PUZZEL_LEGACY_ACCESS_TOKEN = "PUZZEL_LEGACY_ACCESS_TOKEN";
export const PUZZEL_LEGACY_CUSTOMER_KEY = "PUZZEL_LEGACY_CUSTOMER_KEY";

const puzzelHttpClient = axios.create();
const logSource = "puzzelHttpClient";

puzzelHttpClient.interceptors.request.use(async (config) => {
  if (isPuzzelOidcEnabled()) {
    const accessToken = await ensurePuzzelAuthenticated();
    // eslint-disable-next-line no-param-reassign -- Interceptor function
    config.url = `${PUZZEL_BASE_URL + getPuzzelCustomerKey()}/${config.url}`;
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${accessToken}`;
    return config;
  }

  // eslint-disable-next-line no-param-reassign -- Interceptor function
  config.url = `${
    PUZZEL_BASE_URL + localStorage.getItem(PUZZEL_LEGACY_CUSTOMER_KEY)
  }/${config.url}`;
  // eslint-disable-next-line no-param-reassign
  config.headers.Authorization = `Bearer ${localStorage.getItem(
    PUZZEL_LEGACY_ACCESS_TOKEN,
  )}`;
  return config;
});

puzzelHttpClient.interceptors.response.use(
  (response) => {
    if (isPuzzelOidcEnabled()) {
      // Proves the token isn't just present but actually valid/authorized for this API.
      resetPuzzelLoginAttempts();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      if (isPuzzelOidcEnabled()) {
        // The request interceptor already ensures a locally-valid-looking token before every
        // request, so getting rejected here means Puzzel itself considers it invalid
        invalidatePuzzelAccessToken();
        const accessToken = await ensurePuzzelAuthenticated();
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        // use axios as originalRequest.url is already the full absolute URL
        // from the first pass through our request interceptor
        return axios(originalRequest);
      }
      const loginResponse = await puzzelLogin();
      localStorage.setItem(
        PUZZEL_LEGACY_ACCESS_TOKEN,
        loginResponse.data?.accessToken,
      );
      originalRequest.headers.Authorization = `Bearer ${loginResponse.data?.accessToken}`;
      // Same reasoning as above: avoid re-running our request interceptor on retry.
      return axios(originalRequest);
    }
    devLog(
      LogLevel.error,
      logSource,
      "Puzzel API error response body",
      error.response?.data,
    );
    log(
      LogLevel.error,
      logSource,
      `Call to ${error.response?.request?.responseURL} failed with status ${error.response?.status}: ${error.message}`,
    );
    return Promise.reject(error);
  },
);

export { puzzelHttpClient };
